/**
 * Domain normalization + validation — intended to become the ONE implementation.
 *
 * A "domain" reaches us as free text from several places (the agency signup
 * field, a brand URL, a competitor the user types, a cited source), and it is
 * used as a KEY in places where an exact match matters: the HubSpot Company
 * search, the cross-tenant DomainProfile cache, favicon lookups. A typo or a
 * pasted `https://www.x.com/page?utm=…` silently becomes a different key, so
 * normalizing before compare/store is load-bearing rather than cosmetic.
 *
 * MIGRATION STATUS — this is NOT yet the only copy (as of 2026-07-30). It was
 * written for the agency-domain gate + the hubspot-sync CRM key, and only the
 * Lambda's own private copy was actually deleted. Four pre-existing
 * implementations remain, untouched on purpose (they work, and rewriting the
 * competitor flows was out of scope for that change):
 *
 *   - `CompetitorsService.normalizeDomain` / `.looksLikeDomain` (api)
 *   - `isValidDomain` (CompetitorMatrixView.tsx)
 *   - `isValidCompetitorDomain` + a local normalizer (BrandOnboarding.tsx)
 *
 * How they relate today (checked, not assumed): the three validators are
 * **character-for-character the same regex** as `looksLikeDomain` below, so
 * validation genuinely agrees. The normalizers are **narrower** — they strip only
 * `https?://` and don't handle userinfo, a port, or another scheme:
 *
 *   input                    normalizeDomain    the legacy copies
 *   contact@agence.com   →   agence.com         contact@agence.com
 *   acme.com:8443        →   acme.com           acme.com:8443
 *   ftp://files.acme.com →   files.acme.com     ftp:            ← wrong, not merely unstripped
 *
 * The first two are caught downstream (the validator rejects them, so they fail
 * closed). The third silently yields a bogus host, which is the one real bug in
 * the set — reason enough to prefer this file at every new call site.
 *
 * Consequence to keep in mind: **editing this file does not change those four
 * call sites.** Migrating them is mechanical and worth doing next time any of
 * them is touched. Until then, "kept in shared so api/frontend/Lambdas agree"
 * is the goal, not yet a guarantee.
 */

/**
 * Strip everything that isn't the hostname and lowercase it: scheme, userinfo,
 * port, path, query, fragment, and a leading `www.`. Does NOT validate — pair
 * it with `looksLikeDomain` when the result is used as a key.
 */
export function normalizeDomain(input: string | undefined | null): string {
  let d = (input || '').trim().toLowerCase();
  if (!d) return '';
  d = d.replace(/^[a-z][a-z0-9+.-]*:\/\//, ''); // scheme
  d = d.split('/')[0].split('?')[0].split('#')[0]; // path / query / fragment
  // Strip userinfo, and salvage the host from a value that is actually an email
  // ("contact@agence.com" → "agence.com") — a plausible thing to paste into a
  // "your domain" field.
  d = d.split('@').pop() || d;
  d = d.split(':')[0]; // port
  return d.replace(/^www\./, '');
}

/**
 * Validate an ALREADY-normalized domain: at least one dot, an alphabetic TLD of
 * 2+ chars, only host-legal characters. Rejects a company name slugged into a
 * fake host ("acme corp", "acmecorp"), which is the failure mode that poisons
 * shared caches.
 */
export function looksLikeDomain(domain: string | undefined | null): boolean {
  const d = (domain || '').trim().toLowerCase();
  if (!d) return false;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) && /\.[a-z]{2,}$/.test(d);
}

/**
 * Normalize + validate in one step. Returns `''` when the input can't be a
 * hostname, so callers can treat falsy as "no usable domain" rather than
 * carrying a junk value forward. This is the function most callers want.
 */
export function toValidDomain(input: string | undefined | null): string {
  const d = normalizeDomain(input);
  return looksLikeDomain(d) ? d : '';
}
